import React, { useState, useRef, useEffect } from 'react';
import { Workflow, Server, Cloud, BarChart3, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { servicesData } from '../data/mock';

const iconMap = {
  Workflow: Workflow,
  Server: Server,
  Cloud: Cloud,
  BarChart3: BarChart3,
};

export const Services = () => {
  const [activeService, setActiveService] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-32 h-32 rounded-full opacity-10 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-primary), transparent)`,
          animationDelay: '1s'
        }}></div>
        <div className="absolute bottom-20 left-10 w-24 h-24 rounded-full opacity-15 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-purple), transparent)`,
          animationDelay: '3s'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <Server className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              Our Services
            </span>
          </div>
          <h2 className="display-md mb-6" style={{ color: 'var(--text-primary)' }}>
            Comprehensive Automation Solutions
          </h2>
          <p className="body-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            From business process automation to technical infrastructure management, 
            we provide end-to-end solutions that transform how your organization operates.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {servicesData.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isActive = activeService === index;
            
            return (
              <div
                key={service.id}
                className={`feature-card cursor-pointer transition-all duration-1000 delay-${index * 200} ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } ${isActive ? 'ring-2' : ''}`}
                style={{
                  ringColor: isActive ? 'var(--accent-primary)' : 'transparent'
                }}
                onMouseEnter={() => setActiveService(index)}
              >
                {/* Service Icon */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    isActive ? 'animate-pulse-glow' : ''
                  }`} style={{
                    background: isActive ? 'var(--accent-primary)' : 'var(--bg-tertiary)'
                  }}>
                    <IconComponent 
                      className="w-8 h-8" 
                      style={{ 
                        color: isActive ? 'var(--bg-primary)' : 'var(--accent-primary)' 
                      }} 
                    />
                  </div>
                  <div>
                    <h3 className="h3 mb-2" style={{ color: 'var(--text-primary)' }}>
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Service Description */}
                <p className="body-md mb-6" style={{ color: 'var(--text-secondary)' }}>
                  {service.description}
                </p>

                {/* Features List */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                      <span className="body-sm" style={{ color: 'var(--text-muted)' }}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Learn More Button */}
                <Button 
                  className="btn-secondary w-full group"
                  onClick={() => scrollToSection('#contact')}
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className={`text-center transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="max-w-2xl mx-auto p-8 rounded-2xl" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            <h3 className="h2 mb-4" style={{ color: 'var(--text-primary)' }}>
              Need a Custom Solution?
            </h3>
            <p className="body-md mb-6" style={{ color: 'var(--text-secondary)' }}>
              Our team of experts can design and implement automation solutions 
              tailored specifically to your unique business requirements.
            </p>
            <Button 
              className="btn-primary"
              onClick={() => scrollToSection('#contact')}
            >
              Discuss Your Project
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};